import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { ModalV2 as Modal } from '@utilities';

import BodyCard from './components/BodyCard';
import HeaderCard from './components/HeaderCard';

import styles from './_index.module.scss';

const PreBuildModal = ({
  activePrompt,
  buildError,
  getPillTooltip,
  getPillValue,
  handleBuild,
  headerIcon,
  isBuildDisabled,
  isBuilding,
  isLoading,
  isModalOpen,
  name,
  prompts,
  routeId,
  routeLabel,
  sameLevelPrompts,
  searchPrompts,
  selectedAnswers,
  setActivePrompt,
  setIsModalOpen,
  setName,
  setSelectedAnswers,
  setTransformedAnswersMap,
  transformedAnswersMap,
  transformPromptSelections,
  type,
}) => {
  const [animationClass, setAnimationClass] = useState('');

  useEffect(() => {
    if (isModalOpen) {
      setAnimationClass('is-opening');
    }
  }, [isModalOpen]);

  const handleClose = () => {
    setAnimationClass('is-closing');
    setTimeout(() => {
      setIsModalOpen(false);
      setAnimationClass('');
    }, 300);
  };

  useEffect(() => {
    if (!activePrompt) return;
    const rawAnswers = selectedAnswers[activePrompt.promptKey] || [];

    try {
      const transformed = transformPromptSelections({ answers: rawAnswers, prompt: activePrompt });
      setTransformedAnswersMap((prev) => ({
        ...prev,
        [activePrompt.promptKey]: transformed,
      }));
    } catch (error) {
      console.error('Error transforming prompt selections:', error);
    }
  }, [selectedAnswers, activePrompt, setTransformedAnswersMap, transformPromptSelections]);

  const handleAnswerChange = (newAnswers) => {
    if (!activePrompt) return;
    setSelectedAnswers((prev) => ({
      ...prev,
      [activePrompt.promptKey]: newAnswers,
    }));
  };

  return (
    <Modal
      className={classNames(styles['prebuild-modal'], {
        [styles['is-opening']]: animationClass === 'is-opening',
        [styles['is-closing']]: animationClass === 'is-closing',
      })}
      hasCloseIcon={false}
      isActive={isModalOpen}
      size="fullscreen"
    >
      <div className={styles['modal-build-survey']}>
        <div className={styles['modal-build-survey-content']}>
          <HeaderCard
            buildError={buildError}
            handleClose={handleClose}
            headerIcon={headerIcon}
            label={routeLabel}
            name={name}
            setName={setName}
          />
          <BodyCard
            activePrompt={activePrompt}
            getPillTooltip={getPillTooltip}
            getPillValue={getPillValue}
            handleBuild={handleBuild}
            handleClose={handleClose}
            isBuildDisabled={isBuildDisabled}
            isBuilding={isBuilding}
            isLoading={isLoading}
            narrativeId={routeId}
            onAnswerChange={handleAnswerChange}
            prompts={prompts}
            sameLevelPrompts={sameLevelPrompts}
            searchPrompts={searchPrompts}
            selectedAnswers={selectedAnswers}
            setActivePrompt={setActivePrompt}
            setSelectedAnswers={setSelectedAnswers}
            transformedAnswersMap={transformedAnswersMap}
            type={type}
          />
        </div>
      </div>
    </Modal>
  );
};

PreBuildModal.propTypes = {
  activePrompt: PropTypes.object,
  buildError: PropTypes.string,
  getPillTooltip: PropTypes.func,
  getPillValue: PropTypes.func,
  handleBuild: PropTypes.func.isRequired,
  headerIcon: PropTypes.node,
  isBuildDisabled: PropTypes.bool.isRequired,
  isBuilding: PropTypes.bool,
  isLoading: PropTypes.bool,
  isModalOpen: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  prompts: PropTypes.array.isRequired,
  routeId: PropTypes.string,
  routeLabel: PropTypes.string,
  sameLevelPrompts: PropTypes.array,
  searchPrompts: PropTypes.array,
  selectedAnswers: PropTypes.object.isRequired,
  setActivePrompt: PropTypes.func.isRequired,
  setIsModalOpen: PropTypes.func.isRequired,
  setName: PropTypes.func.isRequired,
  setSelectedAnswers: PropTypes.func.isRequired,
  setTransformedAnswersMap: PropTypes.func.isRequired,
  transformedAnswersMap: PropTypes.object.isRequired,
  transformPromptSelections: PropTypes.func.isRequired,
  type: PropTypes.string,
};

export default PreBuildModal;
